import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './lmx-tour.css';
import { LAYOUT_V2 } from '../layout/layoutV2';

/**
 * LmxTour — onboarding guiado por el orbe de LMX.
 *
 * Paso a paso (proyecto, créditos, herramientas, ejecutar todo o un nodo, timeline,
 * galería, redes, MCP): un botón real del Studio se ilumina (el resto queda en
 * penumbra), el orbe vuela a su lado y explica en una burbuja; "Ok" avanza.
 * Al final, una demo TEATRAL (simulada, no gasta créditos ni toca el
 * proyecto): el orbe se sienta en la barra de LMX, "escribe" un pedido, aparecen
 * 3 nodos conectados, viajan al timeline y cierra con un llamado a crear.
 *
 * No depende de nada del Studio salvo selectores data-testid. Si un objetivo
 * no existe o no se ve, ese paso se saltea solo.
 *
 * Props:
 *   orb        url del orbe (assets.orb)
 *   open       boolean
 *   onClose(reason)   'done' | 'skip' | 'cta'
 *   onCta()    el usuario eligió "Crear con LMX" (abrir el dock y enfocar)
 *   onStart()  al abrir: dejar el Studio "limpio" (cerrar timeline, plegar dock)
 *   startAt    id de paso opcional para empezar ahí (por ejemplo 'demo')
 */

export const TOUR_STEPS = [
  {
    id: 'hello', target: null,
    title: 'Hola, soy LMX',
    text: 'Te muestro el Studio en un par de minutos. Todo lo que vas a ver se puede hacer a mano… o pidiéndomelo a mí.',
    ok: 'Empezar', alt: 'Ahora no',
  },
  {
    id: 'project', target: '[data-testid="current-project-indicator"]', place: 'bottom',
    title: 'Tu proyecto',
    text: 'Todo lo que hacés vive en un proyecto: los nodos, sus conexiones y prompts, el timeline y tu charla conmigo. Si ves un punto amarillo al lado del nombre, hay cambios sin guardar.',
  },
  {
    id: 'save', // UI-2: con la barra única, guardar y proyectos viven en el menú del nombre del proyecto
    target: ['[data-testid="save-project-icon-btn"]', '[data-testid="projects-btn"]', '[data-testid="current-project-indicator"]'], place: 'bottom',
    title: 'Guardar y cambiar de proyecto',
    text: 'Guardá con este botón (también se guarda solo cada pocos minutos). Desde Proyectos abrís otro o creás uno nuevo; si tenés algo sin guardar, te aviso antes.',
  },
  {
    id: 'credits', target: '[data-testid="credits-badge"]', place: 'bottom',
    title: 'Tus créditos',
    text: 'Cada generación (imagen, video, voz, música) gasta créditos. Siempre ves cuánto cuesta antes de confirmar. Cuando se te acaben, cargás más tocando acá.',
  },
  {
    id: 'palette', target: '[data-testid="tool-palette"]', place: 'right',
    title: 'Herramientas',
    text: 'Cada herramienta es un nodo: imagen, video, voz, música. Las arrastrás al escenario.',
  },
  {
    id: 'canvas', target: '[data-testid="canvas"]', place: 'inside',
    title: 'El escenario',
    text: 'Acá armás tu flujo conectando nodos: lo que sale de uno entra en el siguiente.',
  },
  {
    id: 'run', target: '[data-testid="run-workflow-btn"]', place: 'bottom',
    title: 'Ejecutar el flujo completo',
    text: 'Una forma de ejecutar: corre todos los nodos en orden, de punta a punta. Antes de gastar créditos siempre ves el costo.',
  },
  {
    id: 'node-run',
    target: [
      '.react-flow__node [data-testid$="process-btn"]',
      '.react-flow__node [data-testid$="generate-btn"]',
      '.react-flow__node [data-testid^="process-"]',
    ],
    place: 'bottom',
    title: 'Ejecutar un solo nodo',
    text: 'La otra forma: en cada nodo vas a encontrar su propio botón de ejecución. Genera sólo ese tramo, sin correr todo el flujo. Ideal para probar y ajustar.',
  },
  {
    id: 'timeline', target: '[data-testid="timeline-toggle-btn"]', place: 'bottom',
    title: 'Timeline',
    text: 'Con lo que generaste armás el video final: clips, textos, música y exportación.',
  },
  {
    id: 'assets', target: '[data-testid="right-panel-tabs"]', place: 'left',
    title: 'Assets y ejecuciones',
    text: 'Todo lo generado y lo que está en curso. Si algo no te sirve, lo detenés desde acá y no se cobra.',
  },
  {
    // I-48: la Galería vive en el panel de funciones del canvas (abajo a la izquierda)
    id: 'gallery', target: ['[data-testid="canvas-gallery-btn"]', '[data-testid="gallery-btn"]'], place: 'right',
    title: 'Galería',
    text: 'Todo lo que creaste queda guardado acá, siempre a mano.',
  },
  {
    id: 'social', target: ['.nav-settings-dropdown', '[data-testid="nav-social-settings"]', '[data-testid="settings-menu-btn"]'], place: 'bottom',
    title: 'Tus redes sociales',
    text: 'Desde el menú de tu nombre conectás tus redes sociales para publicar lo que creás directo desde el Studio.',
  },
  {
    // I-51: MCP vive en el menú del usuario (su nombre, arriba a la derecha)
    id: 'mcp', target: ['[data-testid="mcp-panel-trigger"]', '[data-testid="settings-menu-btn"]'], place: 'bottom',
    requires: 'mcp',
    title: 'Creá desde tu celular',
    text: 'Con MCP vinculás Studio Media Labs con tu agente de IA (por ejemplo, Claude en tu celular). Le pedís lo que necesitás desde donde estés y él lo arma acá por vos.',
  },
  {
    id: 'lmx', target: ['[data-testid="lmx-dock-input"]', '[data-testid="lmx-dock-orb"]'],
    // UI-3: en el layout nuevo LMX está en la columna derecha → la burbuja va a su izquierda
    place: LAYOUT_V2 ? 'left' : 'top',
    title: 'Y lo más importante',
    text: 'Todo esto me lo podés pedir a mí, con tus palabras. Mirá cómo lo hago.',
    ok: 'Ver demo',
  },
  { id: 'demo', demo: true },
  {
    // Envío AP-1 · al final del recorrido, dónde están los videos instructivos.
    id: 'learning', target: '[data-testid="help-menu-btn"]', place: 'bottom',
    title: 'Seguí aprendiendo',
    text: 'Los videos instructivos viven en «?». Cada capítulo te muestra una forma de usar la plataforma, y los nuevos aparecen ahí a medida que se publican.',
    action: { label: 'Ver videos instructivos', reason: 'learning' },
  },
  {
    id: 'cta', target: null,
    title: '¿Arrancamos?',
    text: 'Contame qué querés crear: una publicidad, un video con voz, una imagen de producto… Yo armo el flujo y vos decidís.',
    ok: 'Crear con LMX', alt: 'Explorar solo', cta: true,
  },
];

const DEMO_PROMPT = 'Armame una publicidad: foto del producto, video de 5 s y música.';
const DEMO_NODES = [
  { t: 'Imagen', m: 'Foto de producto', c: '#7fb2ff' },
  { t: 'Video', m: '5 s · cámara lenta', c: '#b48cff' },
  { t: 'Música', m: 'Lo-fi suave', c: '#5fe0c0' },
];

const ORB = 76;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const reduceMotion = () => typeof window !== 'undefined'
  && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Primer elemento visible para un selector (o lista de selectores). */
const visibleRect = (sel) => {
  if (!sel) return null;
  const list = Array.isArray(sel) ? sel : [sel];
  for (const one of list) {
    for (const el of document.querySelectorAll(one)) {
      const r = el.getBoundingClientRect();
      if (r.width < 4 || r.height < 4) continue;
      if (r.bottom < 0 || r.top > window.innerHeight || r.right < 0 || r.left > window.innerWidth) continue;
      return r;
    }
  }
  return null;
};

/** Dónde va el orbe y su burbuja respecto del objetivo. */
const layoutFor = (rect, place) => {
  const W = window.innerWidth; const H = window.innerHeight;
  const pad = 18; const bubbleW = Math.min(340, W - 32);
  if (!rect) {
    return { orb: { x: W / 2 - ORB / 2, y: H * 0.36 - ORB / 2 }, bubble: { x: W / 2 - bubbleW / 2, y: H * 0.36 + ORB / 2 + 14 }, bubbleW, arrow: 'top' };
  }
  let ox; let oy; let bx; let by; let arrow;
  if (place === 'right') {
    ox = rect.right + pad; oy = rect.top + Math.min(rect.height / 2, 160) - ORB / 2;
    bx = ox; by = oy + ORB + 12; arrow = 'top';
  } else if (place === 'left') {
    ox = rect.left - pad - ORB; oy = rect.top + 20;
    bx = ox + ORB - bubbleW; by = oy + ORB + 12; arrow = 'top-right';
  } else if (place === 'top') {
    ox = rect.left + rect.width / 2 - ORB / 2; oy = rect.top - pad - ORB - 120;
    bx = ox + ORB / 2 - bubbleW / 2; by = oy + ORB + 10; arrow = 'top';
  } else if (place === 'inside') {
    ox = rect.left + rect.width / 2 - ORB / 2; oy = rect.top + rect.height * 0.28;
    bx = ox + ORB / 2 - bubbleW / 2; by = oy + ORB + 12; arrow = 'top';
  } else { // bottom
    ox = rect.left + rect.width / 2 - ORB / 2; oy = rect.bottom + pad;
    bx = ox + ORB / 2 - bubbleW / 2; by = oy + ORB + 12; arrow = 'top';
  }
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  ox = clamp(ox, 12, W - ORB - 12); oy = clamp(oy, 12, H - ORB - 200);
  bx = clamp(bx, 16, W - bubbleW - 16); by = clamp(by, 16, H - 190);
  return { orb: { x: ox, y: oy }, bubble: { x: bx, y: by }, bubbleW, arrow };
};

export const LmxTour = ({ orb, open, onClose, onCta, onStart, startAt }) => {
  const steps = TOUR_STEPS;
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [demo, setDemo] = useState(null); // estado de la escena teatral
  const okRef = useRef(null);
  const step = steps[i];

  // Saltear pasos cuyo objetivo no existe o no se ve.
  const go = useCallback((from, dir = 1) => {
    let k = from;
    while (k >= 0 && k < steps.length) {
      const s = steps[k];
      // UI-3: el paso de MCP sólo para quien tiene MCP habilitado
      const allowed = s.requires !== 'mcp'
        || !!document.querySelector('[data-testid="mcp-panel-trigger"], [data-mcp-access="1"]');
      if (allowed && (!s.target || s.demo || visibleRect(s.target))) { setI(k); return; }
      k += dir;
    }
    onClose?.('done');
  }, [steps, onClose]);

  const onStartRef = useRef(onStart);
  onStartRef.current = onStart;
  useEffect(() => {
    if (!open) return;
    onStartRef.current?.();
    setDemo(null);
    // un respiro para que el layout se acomode (timeline cerrado) antes de medir
    const at = startAt ? steps.findIndex((s) => s.id === startAt) : 0;
    const t = setTimeout(() => go(at > 0 ? at : 0), 120);
    return () => clearTimeout(t);
  }, [open, go, startAt, steps]);

  // Medir el objetivo (y seguirlo si la ventana cambia).
  useLayoutEffect(() => {
    if (!open || !step || step.demo) return undefined;
    const measure = () => setRect(visibleRect(step.target));
    measure();
    window.addEventListener('resize', measure);
    const t = setInterval(measure, 500);
    return () => { window.removeEventListener('resize', measure); clearInterval(t); };
  }, [open, step]);

  useEffect(() => { if (open) setTimeout(() => okRef.current?.focus(), 350); }, [open, i]);

  // Esc = saltear.
  useEffect(() => {
    if (!open) return undefined;
    const k = (e) => { if (e.key === 'Escape') onClose?.('skip'); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [open, onClose]);

  // ── La demo teatral ─────────────────────────────────────────
  useEffect(() => {
    if (!open || !step?.demo) return undefined;
    let alive = true;
    const fast = reduceMotion();
    (async () => {
      const input = visibleRect('[data-testid="lmx-dock-input"]');
      const canvas = visibleRect('[data-testid="canvas"]')
        || { left: 0, top: 120, width: window.innerWidth, height: window.innerHeight - 200 };
      const tl = visibleRect('[data-testid="timeline-toggle-btn"]');
      const seat = input
        ? { x: input.left - ORB + 10, y: input.top + input.height / 2 - ORB / 2 }
        : { x: window.innerWidth - ORB - 24, y: window.innerHeight - ORB - 24 };
      setDemo({ phase: 'sit', seat, typed: '', input, canvas, tl, shown: 0 });
      await sleep(fast ? 50 : 700);
      for (let c = 1; c <= DEMO_PROMPT.length && alive; c += 1) {
        setDemo((d) => d && { ...d, phase: 'type', typed: DEMO_PROMPT.slice(0, c) });
        await sleep(fast ? 0 : 28);
      }
      if (!alive) return;
      setDemo((d) => d && { ...d, phase: 'send' });
      await sleep(fast ? 50 : 650);
      for (let n = 1; n <= DEMO_NODES.length && alive; n += 1) {
        setDemo((d) => d && { ...d, phase: 'build', shown: n });
        await sleep(fast ? 50 : 650);
      }
      await sleep(fast ? 50 : 700);
      if (!alive) return;
      setDemo((d) => d && { ...d, phase: 'ship' });
      await sleep(fast ? 50 : 1100);
      if (!alive) return;
      setDemo((d) => d && { ...d, phase: 'done' });
      await sleep(fast ? 50 : 1500);
      if (alive) { setDemo(null); go(i + 1); }
    })();
    return () => { alive = false; };
  }, [open, step, go, i]);

  if (!open || !step) return null;

  // ── Escena de la demo ───────────────────────────────────────
  if (step.demo) {
    if (!demo) return <div className="lmx-tour lmx-tour-dim" />;
    const { seat, typed, input, canvas, tl, shown, phase } = demo;
    const cw = Math.min(760, canvas.width - 80);
    const nodeW = 190; const gap = (cw - nodeW * 3) / 2;
    const top = canvas.top + canvas.height * 0.32;
    const left0 = canvas.left + (canvas.width - cw) / 2;
    const shipTo = tl ? { x: tl.left + tl.width / 2, y: tl.top + tl.height / 2 } : { x: window.innerWidth / 2, y: window.innerHeight };
    return (
      <div className="lmx-tour lmx-tour-dim is-demo" aria-live="polite">
        {input && (
          <div className={`lmx-tour-typing ${phase === 'send' ? 'is-sent' : ''}`}
               style={{ left: input.left, top: input.top, width: input.width, height: input.height }}>
            <span>{typed}</span><i className="lmx-tour-caret" />
          </div>
        )}
        <svg className="lmx-tour-wires" width="100%" height="100%">
          {DEMO_NODES.slice(0, Math.max(0, shown - 1)).map((_, k) => {
            const x1 = left0 + k * (nodeW + gap) + nodeW; const x2 = x1 + gap; const y = top + 46;
            return <path key={k} d={`M${x1} ${y} C ${x1 + gap / 2} ${y}, ${x2 - gap / 2} ${y}, ${x2} ${y}`} className={phase === 'ship' || phase === 'done' ? 'is-gone' : ''} />;
          })}
        </svg>
        {DEMO_NODES.slice(0, shown).map((n, k) => {
          const x = left0 + k * (nodeW + gap); const y = top;
          const ship = phase === 'ship' || phase === 'done';
          const dx = shipTo.x - (x + nodeW / 2); const dy = shipTo.y - (y + 46);
          return (
            <div key={n.t} className={`lmx-tour-node ${ship ? 'is-ship' : ''}`}
                 style={{ left: x, top: y, width: nodeW, '--c': n.c, '--dx': `${dx}px`, '--dy': `${dy}px`, transitionDelay: ship ? `${k * 120}ms` : '0ms' }}>
              <header><i />{n.t}<small>#{k + 1}</small></header>
              <div className="lmx-tour-node-thumb" />
              <p>{n.m}</p>
            </div>
          );
        })}
        <div className={`lmx-tour-orb is-seated ${phase === 'type' ? 'is-talking' : ''}`} style={{ transform: `translate(${seat.x}px, ${seat.y}px)` }}>
          <img src={orb} alt="" />
        </div>
        {phase === 'done' && (
          <div className="lmx-tour-toast">Listo: 3 nodos en el flujo y en el timeline <small>· demo, no gastó créditos</small></div>
        )}
        <button type="button" className="lmx-tour-skip" onClick={() => { setDemo(null); go(i + 1); }}>Saltar demo</button>
      </div>
    );
  }

  // ── Paso normal ─────────────────────────────────────────────
  const L = layoutFor(rect, step.place);
  const hole = rect ? {
    left: rect.left - 8, top: rect.top - 8, width: rect.width + 16, height: rect.height + 16,
  } : null;
  const total = steps.filter((s) => !s.demo).length;
  const num = steps.slice(0, i + 1).filter((s) => !s.demo).length;
  const finish = (reason) => onClose?.(reason);

  return (
    <div className="lmx-tour" role="dialog" aria-modal="true" aria-label={`Recorrido de LMX: ${step.title}`}>
      {hole
        ? <div className="lmx-tour-hole" style={hole} />
        : <div className="lmx-tour-dim" />}
      <div className="lmx-tour-orb" style={{ transform: `translate(${L.orb.x}px, ${L.orb.y}px)` }}>
        <img src={orb} alt="" />
      </div>
      <div className={`lmx-tour-bubble arrow-${L.arrow}`} key={step.id}
           style={{ left: L.bubble.x, top: L.bubble.y, width: L.bubbleW }}>
        <h4>{step.title}</h4>
        <p>{step.text}</p>
        <footer>
          {num > 1 && !step.cta && <span className="lmx-tour-count">{num - 1}/{total - 2}</span>}
          <span className="lmx-tour-sp" />
          {step.alt && (
            <button type="button" className="lmx-tour-ghost"
                    onClick={() => finish(step.cta ? 'done' : 'skip')}>{step.alt}</button>
          )}
          {step.action && (
            <button type="button" className="lmx-tour-ghost" data-testid={`tour-action-${step.id}`}
                    onClick={() => finish(step.action.reason)}>{step.action.label}</button>
          )}
          {!step.alt && !step.cta && !step.action && (
            <button type="button" className="lmx-tour-ghost" onClick={() => finish('skip')}>Saltar</button>
          )}
          <button type="button" ref={okRef} className="lmx-tour-ok"
                  onClick={() => {
                    if (step.cta) { onCta?.(); finish('cta'); return; }
                    go(i + 1);
                  }}>
            {step.ok || 'Ok'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default LmxTour;
